import React from 'react';
import { useIntl } from 'react-intl';
import { DashboardWidget } from './DashboardWidget';
import { toCalendarDate } from '../../../utils/UtilFunctions';

interface IUpcomingSession {
  sessionId: number;
  clientId: number;
  clientName: string;
  sessionDate: string;
  sessionTime: string | null;
  sessionMode: string | null;
  location: string | null;
  contactMethod: string | null;
  status: string;
  workoutName: string | null;
  /** #6 ítem 4: con esto el nombre del entrenamiento abre el preview. */
  workoutInstanceId?: number | null;
}

interface Props {
  data: IUpcomingSession[];
  loading: boolean;
  onClientClick?: (clientId: number) => void;
  onWorkoutClick?: (workoutInstanceId: number) => void;
}

const SESSION_MODE_ICONS: Record<string, string> = {
  'in-person': 'pi pi-map-marker',
  online: 'pi pi-video',
  hybrid: 'pi pi-globe'
};

export const UpcomingWorkoutsWidget: React.FC<Props> = ({ data, loading, onClientClick, onWorkoutClick }) => {
  const intl = useIntl();

  const formatDate = (dateStr: string) => {
    // #38 ítem 1: la lógica de día-calendario vive en toCalendarDate, compartida
    // con los otros widgets que mostraban el día anterior.
    const dateOnly = toCalendarDate(dateStr);
    if (!dateOnly) return '';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (dateOnly.getTime() === today.getTime()) return intl.formatMessage({ id: 'dashboard.today' });
    if (dateOnly.getTime() === tomorrow.getTime()) return intl.formatMessage({ id: 'dashboard.tomorrow' });
    return dateOnly.toLocaleDateString(intl.locale, { weekday: 'short', day: 'numeric', month: 'short' });
  };

  return (
    <DashboardWidget
      title={intl.formatMessage({ id: 'dashboard.upcomingWorkouts' })}
      icon="pi pi-calendar"
      iconColor="#4d7c0f"
      loading={loading}
      badge={data.length || null}
    >
      {data.length === 0 ? (
        <p style={{ color: 'var(--ios-text-tertiary)', fontSize: '0.85rem', textAlign: 'center', padding: '1rem 0' }}>
          {intl.formatMessage({ id: 'dashboard.noUpcoming' })}
        </p>
      ) : (
        <div className="flex flex-column gap-2">
          {data.slice(0, 6).map((session) => (
            <div
              key={session.sessionId}
              className="flex align-items-center gap-2"
              style={{
                padding: '0.5rem',
                borderRadius: 'var(--ios-radius-md)',
                background: 'var(--ios-surface-subtle)'
              }}
            >
              <i
                className={SESSION_MODE_ICONS[session.sessionMode ?? ''] ?? 'pi pi-user'}
                style={{ color: '#4d7c0f', fontSize: '0.85rem', width: '1.2rem', textAlign: 'center' }}
              />
              {/* #6 ítem 2: el nombre del alumno y el del entrenamiento quedaban
                  pegados desde que se separaron en dos botones. El gap los separa
                  sin tocar cada uno por su lado. */}
              <div className="flex flex-column flex-grow-1" style={{ minWidth: 0, gap: '0.2rem' }}>
                {/* #6 ítem 4: el nombre de la persona va al perfil y el del
                    entrenamiento abre el preview. Antes toda la fila iba al
                    perfil, así que no había forma de ver el entrenamiento. */}
                <button
                  type="button"
                  onClick={() => onClientClick?.(session.clientId)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    textAlign: 'left',
                    fontFamily: 'inherit',
                    fontWeight: 600,
                    fontSize: '0.82rem',
                    color: 'var(--ios-text)',
                    cursor: onClientClick ? 'pointer' : 'default',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%'
                  }}
                >
                  {session.clientName}
                </button>
                {session.workoutInstanceId && onWorkoutClick ? (
                  <button
                    type="button"
                    onClick={() => onWorkoutClick(session.workoutInstanceId as number)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      textAlign: 'left',
                      fontFamily: 'inherit',
                      fontSize: '0.72rem',
                      color: 'var(--ios-text-tertiary)',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      textDecorationStyle: 'dotted',
                      textUnderlineOffset: '2px',
                      maxWidth: '100%'
                    }}
                  >
                    {session.workoutName ?? intl.formatMessage({ id: 'dashboard.session' })}
                  </button>
                ) : (
                  <div style={{ fontSize: '0.72rem', color: 'var(--ios-text-tertiary)' }}>
                    {session.workoutName ?? intl.formatMessage({ id: 'dashboard.session' })}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#4d7c0f' }}>
                  {formatDate(session.sessionDate)}
                </div>
                {session.sessionTime && (
                  <div style={{ fontSize: '0.68rem', color: 'var(--ios-text-tertiary)' }}>{session.sessionTime}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardWidget>
  );
};
